import { FC } from 'react';
import { Typography, Box } from '@mui/material';
import { AnnouncementFormData } from '../interfaces/AnnouncementFormData';
import { AttachmentsWrapper } from '../style';
import DragIndicatorIcon from 'components/Icons/dragIndicatorIcon';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { CustomIconButton } from 'components/Dropdown/LocationsSelectItemsList/styles';
import CloseIcon from 'components/Icons/closeIcon';
import AttachmentIcon from '../AttachmentIcon/attachmentsIcon';
import { formatBytes } from 'utils/globalFunction';
import { useDispatch } from 'react-redux';
import DIALOGS from 'utils/dialogIds';
import actions from 'store/actions';

interface AnnouncementsBuilderFormProps {
  formData: any;
  setFormData?: (data: AnnouncementFormData) => void;
  reviewMode?: boolean;
  compact?: boolean;
  showSize?: boolean;
  allowPreview?: boolean;
  iconSize?: 'large' | 'medium' | 'small';
  listProps?: any;
}

const AnnouncementAttachments: FC<AnnouncementsBuilderFormProps> = ({
  formData,
  setFormData,
  reviewMode = false,
  compact = false,
  showSize = false,
  allowPreview = false,
  iconSize = 'small',
  listProps,
}) => {
  const dispatch = useDispatch();
  const onDragEnd = (result) => {
    // Dropped outside the list
    if (!result.destination) return;

    const items = Array.from(formData.attachments);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setFormData?.({
      ...formData,
      attachments: items,
    });
  };

  // Handle attachment removal
  const removeAttachment = (index) => {
    const newAttachments = [...formData.attachments];
    newAttachments.splice(index, 1);
    setFormData?.({
      ...formData,
      attachments: newAttachments,
    });
  };
  const allImages = formData?.attachments
    ?.filter((file: any) => {
      return (
        file?.fileType === 'image' ||
        file?.type?.includes('image') ||
        file?.fileType === 'video' ||
        file?.type?.includes('video') ||
        file?.fileType === 'pdf' ||
        file?.type?.includes('pdf') ||
        file?.fileType === 'doc' ||
        file?.type?.includes('doc') ||
        file?.fileType === 'sop' ||
        file?.type?.includes('sop')
      );
    })
    .map((file: any) => {
      return {
        ...file,
        url: file?.path ?? file?.url,
        name: file?.name || file?.title,
      };
    });
  const handleViewFullscreen = (file: any, index) => {
    if (!allowPreview) return;
    const isVideo = file?.fileType === 'video' || file?.type?.includes('video');
    const isImage = file?.fileType === 'image' || file?.type?.includes('image');
    const isPdf = file?.fileType === 'pdf' || file?.type?.includes('pdf');
    const isDoc = file?.fileType === 'doc' || file?.type?.includes('doc');
    const isSop = file?.fileType === 'sop' || file?.type?.includes('sop');
    if (isImage || isVideo || isPdf || isDoc || isSop) {
      dispatch(
        actions.setDialog({
          dialogId: DIALOGS.PHOTOS_ATTACHMENTS_PREVIEW,
          open: true,
          data: {
            allAttachments: allImages,
            id: file?.path ?? file?.url,
          },
        }),
      );
    }
  };
  return formData?.attachments?.length ? (
    <AttachmentsWrapper
      sx={{ ...(compact ? { padding: '0', border: 'none' } : {}) }}
      {...listProps}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="attachments">
          {(provided) => (
            <Box
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{
                listStyle: 'none',
                padding: 0,
                cursor: reviewMode ? 'pointer' : 'default',
              }}
            >
              {formData.attachments.map((file: any, index) => (
                <Draggable
                  key={index}
                  draggableId={`attachment-${index}`}
                  index={index}
                  isDragDisabled={reviewMode}
                >
                  {(provided, draggableSnapshot) => (
                    <Box
                      className="attachementItem"
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      onClick={() => handleViewFullscreen(file, index)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: compact ? '6px' : '8px 12px',
                        margin: '4px 0',
                        ...(draggableSnapshot.isDragging && {
                          backgroundColor: '#e3e3ff',
                          border: '1px solid #7070d8',
                          borderRadius: '12px',
                          boxShadow: '0px 4px 12px 0px #00000033',
                        }),
                        ...(compact
                          ? { borderRadius: '8px', border: '1px solid #E0E0E0' }
                          : {}),
                        ...provided.draggableProps.style,
                      }}
                    >
                      {!reviewMode ? (
                        <Box
                          {...provided.dragHandleProps}
                          sx={{ mr: 1, cursor: 'grab' }}
                        >
                          <DragIndicatorIcon fontSize="small" />
                        </Box>
                      ) : null}
                      <Box
                        className="itemCnt"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <AttachmentIcon
                          fileType={
                            file?.type ||
                            file?.meta?.headInfo?.['content-type'] ||
                            ''
                          }
                          size={iconSize || 'medium'}
                          file={file}
                        />
                        <Typography
                          className="itemName"
                          fontSize={compact ? 11 : 15}
                          fontWeight={compact ? 700 : 600}
                          color={'#212121'}
                          sx={{
                            width: 'auto',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                        >
                          {file?.name || file?.title}
                        </Typography>
                        {showSize ? (
                          <Typography
                            color={'#616161'}
                            fontSize={11}
                            fontWeight={400}
                          >
                            - {formatBytes(file?.size || file?.meta?.size)}
                          </Typography>
                        ) : null}
                        {!reviewMode ? (
                          <CustomIconButton
                            size="small"
                            onClick={() => removeAttachment(index)}
                            sx={{ ml: 'auto' }}
                          >
                            <CloseIcon
                              fontSize="small"
                              sx={{ path: { fill: '#212121' } }}
                            />
                          </CustomIconButton>
                        ) : null}
                      </Box>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </AttachmentsWrapper>
  ) : null;
};

export default AnnouncementAttachments;
